import { ActionIcon, Loader, Tooltip } from '@mantine/core'
import { Mic, Square } from 'lucide-react'
import type { VoiceRecorderState } from '../lib/useVoiceRecorder'

type VoiceInputButtonProps = {
  state: VoiceRecorderState
  levelHistory: number[]
  onToggle: () => void
  disabled?: boolean
}

// The stop button is size="sm" (26px in Mantine v8). Match bar height to that.
const BAR_MAX_HEIGHT = 26
const BAR_MIN_HEIGHT = 3

function AudioLevelIndicator({ levels }: { levels: number[] }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3, height: BAR_MAX_HEIGHT }}>
      {levels.map((level, i) => {
        // Non-linear scaling: sqrt so quiet signals are clearly distinct from silence
        const scaled = Math.sqrt(level)
        const height = BAR_MIN_HEIGHT + scaled * (BAR_MAX_HEIGHT - BAR_MIN_HEIGHT)
        return (
          <div
            key={i}
            style={{
              width: 3,
              height,
              borderRadius: 1.5,
              backgroundColor: 'var(--mantine-color-red-6)',
            }}
          />
        )
      })}
    </div>
  )
}

export default function VoiceInputButton({ state, levelHistory, onToggle, disabled }: VoiceInputButtonProps) {
  if (state === 'transcribing') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 4 }}>
        <Loader size={18} />
      </div>
    )
  }

  if (state === 'recording') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 4 }}>
        <AudioLevelIndicator levels={levelHistory} />
        <Tooltip label="Stop recording">
          <ActionIcon variant="filled" color="red" size="sm" onClick={onToggle} aria-label="Stop recording">
            <Square size={14} />
          </ActionIcon>
        </Tooltip>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 4 }}>
      <Tooltip label="Voice input">
        <ActionIcon variant="subtle" color="gray" size="sm" onClick={onToggle} disabled={disabled} aria-label="Voice input">
          <Mic size={18} />
        </ActionIcon>
      </Tooltip>
    </div>
  )
}
